import { Helmet } from "react-helmet-async";
import type { ReactNode } from "react";

interface Props {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  jsonLd?: object[];
  noindex?: boolean;
  type?: "website" | "article";
  article?: { publishedTime?: string; modifiedTime?: string; author?: string };
  children?: ReactNode;
}

const SITE_URL = "https://corianpark.au";

function deriveCanonical(): string {
  const env = import.meta.env.VITE_SITE_URL as string | undefined;
  const base = env ? env.replace(/\/$/, "") : SITE_URL;
  if (typeof window === "undefined") return base;
  return `${base}${window.location.pathname}`;
}

export function SEO({
  title,
  description,
  canonical,
  ogImage,
  jsonLd,
  noindex = false,
  type = "website",
  article,
  children,
}: Props) {
  const fullTitle = title.endsWith("Corian Park") ? title : `${title} | Corian Park`;
  const url = canonical ?? deriveCanonical();
  const image = ogImage ?? `${SITE_URL}/og-image.webp`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Corian Park" />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {type === "article" && article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {type === "article" && article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {type === "article" && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {jsonLd?.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
      {children}
    </Helmet>
  );
}
